import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import CenterModal from '../components/CenterModal';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', href: '/' },
    { name: 'Gebäudereinigung', href: '/gebaeudereinigung' },
    { name: 'Fliesenlegen', href: '/fliesenlegen' },
    { name: 'Über Uns', href: '/ueber-uns' },
    { name: 'Kontakt', href: '/kontakt' },
  ];

  // Scroll detection for navbar background
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const isActivePage = (href) => location.pathname === href;

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className={`fixed w-full z-50 transition-all duration-500 ${isScrolled
        ? 'bg-white/95 backdrop-blur-md shadow-xl border-b border-gray-100'
        : 'bg-white/80 backdrop-blur-sm shadow-lg'
        }`}
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20 md:h-24 lg:h-28">
          {/* Logo */}
          <motion.div
            className="flex-shrink-0"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link
              to="/"
              className={`relative flex items-center focus:outline-none rounded-lg p-1 transition-all duration-300 hover:opacity-90 ${isActivePage('/')
                ? 'border-2 border-green-400 bg-green-50'
                : 'hover:bg-gray-50'
                }`}
            >
              <img
                src="/MGS-Logo.png"
                alt="MGS Gebäudeservice Logo"
                className="h-12 sm:h-14 md:h-16 lg:h-18 xl:h-20 w-auto object-contain"
              />
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="ml-6 lg:ml-10 flex items-baseline space-x-4 lg:space-x-8">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.name}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 + 0.3 }}
                >
                  <Link
                    to={link.href}
                    className={`relative px-2 lg:px-4 py-2 transition-all duration-300 rounded-lg lg:rounded-xl group text-sm lg:text-base shadow-sm hover:shadow-modern ${isActivePage(link.href)
                      ? 'text-green-700 bg-green-100/80 shadow-green'
                      : 'text-gray-700 hover:text-green-600 hover:bg-gray-50'
                      }`}
                  >
                    {link.name}

                    {/* Active indicator */}
                    {isActivePage(link.href) && (
                      <motion.div
                        layoutId="activeTab"
                        className="absolute bottom-1 left-2 right-2 h-0.5 bg-gradient-to-r from-green-500 to-green-600 rounded-full shadow-green"
                        initial={false}
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                      />
                    )}

                    {/* Hover effect */}
                    <div className="absolute inset-0 rounded-xl bg-green-100/30 scale-0 group-hover:scale-100 transition-transform duration-300 -z-10"></div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>

          {/* CTA Button - Desktop & Mobile */}
          <motion.div
            className="flex items-center space-x-2 sm:space-x-3"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
          >
            {/* Mobile Beratung Button - always visible */}
            <button
              onClick={() => setIsContactFormOpen(true)}
              className="md:hidden group relative bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all duration-300 transform hover:scale-105 text-xs sm:text-sm shadow-modern hover:shadow-3d"
            >
              <span className="relative z-10 flex items-center">
                Beratung
                <svg className="w-3 h-3 sm:w-4 sm:h-4 ml-1 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
            </button>

            {/* Desktop Beratung Button */}
            <button
              onClick={() => setIsContactFormOpen(true)}
              className="hidden md:block group relative bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-4 lg:px-8 py-2 lg:py-3 rounded-lg lg:rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-3d-lg overflow-hidden text-sm lg:text-base"
            >
              <span className="relative z-10 flex items-center">
                Beratung
                <svg className="w-4 h-4 lg:w-5 lg:h-5 ml-1 lg:ml-2 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>

              {/* Hover effect background */}
              <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-green-500 translate-x-full group-hover:translate-x-0 transition-transform duration-300"></div>
            </button>
          </motion.div>

          {/* Mobile menu button */}
          <div className="md:hidden ml-3 sm:ml-4">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsOpen(!isOpen)}
              className="relative inline-flex items-center justify-center p-2.5 sm:p-3 rounded-xl text-gray-700 hover:text-green-600 hover:bg-gray-50 focus:outline-none transition-all duration-300 border border-gray-200 shadow-sm hover:shadow-modern"
              aria-expanded="false"
            >
              <span className="sr-only">Hauptmenü öffnen</span>

              {/* Hamburger Icon with Animation */}
              <div className="w-6 h-6 sm:w-7 sm:h-7 relative">
                <motion.span
                  animate={isOpen ? { rotate: 45, y: 0, top: '50%' } : { rotate: 0, y: 0, top: '20%' }}
                  className="absolute left-0 w-6 sm:w-7 h-0.5 bg-current transform origin-center transition-all duration-300"
                  style={{ transformOrigin: 'center' }}
                />
                <motion.span
                  animate={isOpen ? { opacity: 0 } : { opacity: 1 }}
                  className="absolute left-0 top-1/2 w-6 sm:w-7 h-0.5 bg-current transition-all duration-300 transform -translate-y-1/2"
                />
                <motion.span
                  animate={isOpen ? { rotate: -45, y: 0, top: '50%' } : { rotate: 0, y: 0, top: '80%' }}
                  className="absolute left-0 w-6 sm:w-7 h-0.5 bg-current transform origin-center transition-all duration-300"
                  style={{ transformOrigin: 'center' }}
                />
              </div>
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="md:hidden bg-white/95 backdrop-blur-md border-t border-gray-100 shadow-lg"
          >
            <div className="px-3 sm:px-4 pt-2 pb-4 sm:pb-6">
              {/* Split navigation into two columns */}
              <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-4">
                {/* Left column */}
                <div className="space-y-1">
                  {navLinks.slice(0, 2).map((link, index) => (
                    <motion.div
                      key={link.name}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Link
                        to={link.href}
                        className={`block px-2 sm:px-3 py-2 sm:py-3 rounded-lg sm:rounded-xl transition-all duration-300 text-xs sm:text-sm text-center shadow-sm hover:shadow-modern ${isActivePage(link.href)
                          ? 'text-green-700 bg-green-100/80 border-2 border-green-400'
                          : 'text-gray-700 hover:text-green-600 hover:bg-gray-50 border-2 border-transparent'
                          }`}
                      >
                        {link.name}
                      </Link>
                    </motion.div>
                  ))}
                </div>

                {/* Right column */}
                <div className="space-y-1">
                  {navLinks.slice(2).map((link, index) => (
                    <motion.div
                      key={link.name}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: (index + 2) * 0.1 }}
                    >
                      <Link
                        to={link.href}
                        className={`block px-2 sm:px-3 py-2 sm:py-3 rounded-lg sm:rounded-xl transition-all duration-300 text-xs sm:text-sm text-center shadow-sm hover:shadow-modern ${isActivePage(link.href)
                          ? 'text-green-700 bg-green-100/80 border-2 border-green-400'
                          : 'text-gray-700 hover:text-green-600 hover:bg-gray-50 border-2 border-transparent'
                          }`}
                      >
                        {link.name}
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Mobile CTA - Full width */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: navLinks.length * 0.1 + 0.2 }}
                className="pt-2 sm:pt-3"
              >
                <button
                  onClick={() => setIsContactFormOpen(true)}
                  className="block w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white py-3 sm:py-4 px-2 sm:px-3 rounded-lg sm:rounded-xl text-center transition-all duration-300 transform hover:scale-105 text-xs sm:text-sm shadow-3d hover:shadow-3d-lg overflow-hidden"
                >
                  <span className="block truncate">Kostenloses Beratungsgespräch</span>
                </button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Contact Modal */}
      <CenterModal
        isOpen={isContactFormOpen}
        onClose={() => setIsContactFormOpen(false)}
      />
    </motion.nav>
  );
};

export default Navbar;

